import { useMapNavigation } from "@components/world-map/hooks/use-map-navigation";
import { MapStyleThumbnail } from "@components/world-map/map-style-thumbnail";
import { WorldMapButtons } from "@components/world-map/world-map-buttons";
import {
  MapStyle,
  WorldMapButtonGroup,
  WorldMapButtonProps,
} from "@components/world-map/world-map-types";
import {
  Banner,
  GpsFixedIcon,
  MinusIcon,
  PlusIcon,
  ZoomAllIcon,
} from "@flat-ui";
import { Box } from "@mui/material";
import MapView from "@arcgis/core/views/MapView";
import { RefObject } from "react";

interface WorldMapControlsProps {
  /** Ref to the ArcGIS MapView instance */
  viewRef: RefObject<MapView | null>;

  /** Whether to show the full set of navigation controls (zoom, location, style toggle) */
  shouldShowMapControls: boolean;

  /** Adjusts the map view to fit all entities */
  viewAllEntities: () => void;

  /** The currently active map style */
  mapStyle: MapStyle;

  /** Updates the selected map style */
  setMapStyle: (mapStyle: MapStyle) => void;

  /** Additional buttons provided by the parent to append to the control group */
  externalButtons?: WorldMapButtonProps[];
}

interface GetFullControlGroupsProps
  extends Pick<
    WorldMapControlsProps,
    "viewAllEntities" | "mapStyle" | "setMapStyle"
  > {
  /** Custom hook for map navigation */
  navigation: ReturnType<typeof useMapNavigation>;
}

/**
 * Renders map navigation controls (zoom, location, reset-view, style toggle)
 * and the location permission banner.
 * Must only be rendered after the map view is ready.
 */
export function WorldMapControls({
  viewRef,
  shouldShowMapControls,
  viewAllEntities,
  mapStyle,
  setMapStyle,
  externalButtons,
}: WorldMapControlsProps): JSX.Element {
  const navigation = useMapNavigation(viewRef, true);

  const buttonGroups = shouldShowMapControls
    ? getFullControlGroups({
        navigation,
        viewAllEntities,
        mapStyle,
        setMapStyle,
      })
    : getMinimalControlGroups(viewAllEntities);

  const externalGroups: WorldMapButtonGroup[] = (externalButtons ?? []).map(
    (button) => [button]
  );

  return (
    <>
      <WorldMapButtons buttonGroups={[...buttonGroups, ...externalGroups]} />
      {navigation.shouldShowLocationBanner && (
        <Box
          sx={{
            position: "absolute",
            top: 2,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1000,
            maxWidth: "90%",
          }}
        >
          <Banner
            variant="warning"
            title="Sphere XG does not have permission to use your location."
            onClose={navigation.dismissLocationBanner}
          />
        </Box>
      )}
    </>
  );
}

/** Returns a single "Reset view" button group for minimal mode. */
function getMinimalControlGroups(
  viewAllEntities: () => void
): WorldMapButtonGroup[] {
  return [
    [
      {
        icon: <ZoomAllIcon />,
        tooltip: "Reset view",
        onClick: viewAllEntities,
      },
    ],
  ];
}

/** Returns the full set of navigation button groups. */
function getFullControlGroups({
  navigation,
  viewAllEntities,
  mapStyle,
  setMapStyle,
}: GetFullControlGroupsProps): WorldMapButtonGroup[] {
  return [
    [
      {
        icon: <GpsFixedIcon />,
        tooltip: navigation.isLocationDisabled
          ? "Cannot access your location"
          : "Current location",
        onClick: navigation.goToCurrentLocation,
        isDisabled: navigation.isLocationDisabled,
      },
    ],
    [
      {
        icon: <PlusIcon />,
        tooltip: "Zoom in",
        onClick: navigation.zoomIn,
        isDisabled: navigation.isZoomInDisabled,
      },
      {
        icon: <MinusIcon />,
        tooltip: "Zoom out",
        onClick: navigation.zoomOut,
        isDisabled: navigation.isZoomOutDisabled,
      },
    ],
    [
      {
        icon: <ZoomAllIcon />,
        tooltip: "Reset view",
        onClick: viewAllEntities,
      },
    ],
    [
      {
        icon: <MapStyleThumbnail mapStyle={mapStyle} />,
        tooltip:
          mapStyle === "colored" ? "Switch to satellite" : "Switch to colored",
        onClick: () =>
          setMapStyle(mapStyle === "colored" ? "satellite" : "colored"),
      },
    ],
  ];
}
